import type { ActionTree } from "vuex";
import { AppHookCodes } from "@/data/enums/hooks";
import type { IBasketProduct } from "@/models/baskets";
import type {
  IBasket,
  IClient,
  IInvoice,
  ISelf
} from "@upmind-automation/types";
import type { IState } from "@/store";
import type { IUser } from "@/models/users";
import { pushPageUser, UPM_DATA_LAYER } from "@/boot/gtm";
import { deleteCookie, getCookie, setCookie } from "@/helpers/cookies";
import { useAppEvents } from "@/composables/appEvents";
import _ from "@/boot/lodash";
const { pushEvent } = useAppEvents();

const getEcommerceData = (invoice: IInvoice | IBasket) => {
  return {
    currency: invoice?.currency?.code,
    tax: invoice?.tax_amount_converted,
    value: invoice?.net_amount_converted,
    net_value: invoice?.net_amount_converted, // Custom
    gross_value: invoice?.total_amount_converted, // Custom
    transaction_id: invoice?.number,
    purchase_type: "new_purchase",
    coupon: _.map(invoice?.promotions, "promotion.code").join(","),
    items: _.map(invoice?.products, product => ({
      item_id: product?.product?.id,
      item_name: product?.product?.name, // For reporting purposes we intentionally pass untranslated product name
      item_category: product?.product?.category?.name, // For reporting purposes we intentionally pass untranslated category name
      quantity: product?.quantity,
      discount: product?.configuration_net_amount_discount_converted,
      price: product?.configuration_net_amount_discounted_converted,
      net_price: product?.configuration_net_amount_discounted_converted, // Custom
      gross_price: product?.configuration_total_discounted_amount_converted, // Custom
      upm_report_code_1: product?.product?.report_code_1, // item-scoped custom Upmind parameter
      upm_report_code_2: product?.product.report_code_2 // item-scoped custom Upmind parameter
    }))
  };
};

const initialState = () => ({});

const actions: ActionTree<{}, IState> = {
  [AppHookCodes.ACTOR_IDENTIFIED]: async (context, self: ISelf) => {
    try {
      if (self.analytics?.sha_user_id) {
        // Get the `upm_actor` cookie
        const cookie = getCookie("upm_actor");
        // Parse the cookie value (expecting base64 encoded JSON)
        const parsed = cookie ? JSON.parse(atob(cookie)) : null;
        // Define the data to be saved in the cookie
        const data = {
          clean_email: self.analytics?.clean_email,
          customer_status: self.analytics?.customer_type,
          logged_in: self.analytics?.logged_in,
          salted_sha_user_id: self.analytics?.salted_sha_user_id,
          sha_user_id: self.analytics?.sha_user_id
        };
        // Save analytical user data to `upm_actor` cookie
        setCookie("upm_actor", btoa(JSON.stringify(data)), 60 * 60 * 24);
        // If the cookie value has changed, push new page user event
        if (!_.isEqual(data, parsed)) pushPageUser();
      }
    } catch {
      // Malformed cookie, delete it
      deleteCookie("upm_actor");
    }

    pushEvent(
      AppHookCodes.ACTOR_IDENTIFIED,
      _.pick(self, ["actor_id", "role"])
    );
  },
  [AppHookCodes.ADD_TO_BASKET_SUCCESS]: async (
    { rootGetters },
    basketProduct: IBasketProduct
  ) => {
    try {
      if (_.isEmpty(basketProduct)) return;
      const ecommerce = {
        currency: basketProduct.base_currency_code,
        value: basketProduct.configuration_net_amount_discounted_converted,
        tax: rootGetters["utils/money/precisionSum"]([
          basketProduct.configuration_total_discounted_amount_converted,
          -basketProduct.configuration_net_amount_discounted_converted
        ]),
        items: [
          {
            item_id: basketProduct.product.id,
            item_name: basketProduct.product.name, // For reporting purposes we intentionally pass untranslated product name
            item_category: basketProduct.product.category.name, // For reporting purposes we intentionally pass untranslated category name
            quantity: basketProduct.quantity,
            discount: basketProduct.configuration_net_amount_discount_converted,
            duration: basketProduct?.billing_cycle_months,
            price: basketProduct.configuration_net_amount_discounted_converted,
            upm_report_code_1: basketProduct.product.report_code_1, // item-scoped custom Upmind parameter
            upm_report_code_2: basketProduct.product.report_code_2 // item-scoped custom Upmind parameter
          }
        ]
      };
      pushEvent(AppHookCodes.ADD_TO_BASKET_SUCCESS, { ecommerce });
      UPM_DATA_LAYER.push({ event: "add_to_cart", ecommerce, _clear: true });
    } catch {
      // Fail silently
    }
  },
  [AppHookCodes.CHECKOUT_STARTED]: async (context, basket: IBasket) => {
    try {
      if (_.isEmpty(basket)) return;
      const ecommerce = getEcommerceData(basket);
      pushEvent(AppHookCodes.CHECKOUT_STARTED, { ecommerce });
      UPM_DATA_LAYER.push({ event: "begin_checkout", ecommerce, _clear: true });
    } catch {
      // Fail silently
    }
  },
  [AppHookCodes.ORDER_CONVERTED]: async (context, invoice: IInvoice) => {
    try {
      if (_.isEmpty(invoice)) return;
      const ecommerce = getEcommerceData(invoice);
      pushEvent(AppHookCodes.ORDER_CONVERTED, { ecommerce });
      UPM_DATA_LAYER.push({ event: "purchase", ecommerce, _clear: true });
    } catch {
      // Fail silently
    }
  },
  [AppHookCodes.ORDER_PAID]: async (context, invoice: IInvoice) => {
    try {
      if (_.isEmpty(invoice)) return;
      const ecommerce = getEcommerceData(invoice);
      pushEvent(AppHookCodes.ORDER_PAID, { ecommerce });
      UPM_DATA_LAYER.push({ event: "invoice_paid", ecommerce, _clear: true });
    } catch {
      // Fail silently
    }
  },
  [AppHookCodes.ADMIN_USER_LOGGED_IN]: async (
    { dispatch, rootState },
    user: IUser
  ) => {
    await dispatch("ui/userflow/tryInit", user, { root: true });
    /** Here we check if the current brand has a demo data import_id, and
     * initiate a check to see if the import is complete or in progress */
    if (rootState.brand?.demo_data_import_id)
      dispatch("demoData/checkImportStatus", null, { root: true });
  },
  [AppHookCodes.CLIENT_REGISTERED]: (context, clientId: IClient["id"]) => {
    // Push 'sign_up' event + set `upmind.user_id`
    UPM_DATA_LAYER.push({
      event: "sign_up",
      upmind: {
        user_id: clientId
      }
    });
  }
};

export default {
  namespaced: true,
  state: initialState(),
  actions
};
